import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { env } from "../env.js";
import { isZitadel } from "./config.js";

/**
 * One auth interface for the app, backed by either Auth0 or Zitadel.
 *
 * Call sites use this instead of an SDK hook directly, so swapping providers is
 * an env change rather than an edit in every screen.
 *
 * Shape:
 *   user             claims object, or null when signed out
 *   isAuthenticated  boolean
 *   isLoading        boolean
 *   getAccessToken() resolves the access token for API calls
 *   login()          starts the redirect flow
 *   logout({ returnTo })
 *
 * `getAccessToken` is memoized in both adapters: useAuthToken has it in a
 * dependency array, and a fresh function each render would re-run that effect
 * on every render.
 */

function useAuth0Adapter() {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently } =
    useAuth0();

  // The audience stays here so callers never need to know which IdP is behind this.
  const getAccessToken = useCallback(
    () => getAccessTokenSilently({ audience: env.REACT_APP_AUDIENCE }),
    [getAccessTokenSilently]
  );

  const doLogout = useCallback(({ returnTo } = {}) => logout({ returnTo }), [logout]);

  return {
    user: user || null,
    isAuthenticated,
    isLoading,
    getAccessToken,
    login: loginWithRedirect,
    logout: doLogout,
  };
}

function useZitadelAdapter() {
  const auth = useOidcAuth();
  const accessToken = auth.user ? auth.user.access_token : "";

  // Already in memory here; wrapped in a promise so both adapters look alike.
  const getAccessToken = useCallback(async () => accessToken, [accessToken]);

  const login = useCallback(() => auth.signinRedirect(), [auth]);

  // RP-initiated logout. Zitadel validates post_logout_redirect_uri against the
  // Console-registered list, so an unregistered returnTo is rejected there.
  const doLogout = useCallback(
    ({ returnTo } = {}) =>
      auth.signoutRedirect(returnTo ? { post_logout_redirect_uri: returnTo } : undefined),
    [auth]
  );

  // `activeNavigator` is set while a navigator runs. Only the redirect ones belong in
  // `isLoading`: the browser is about to leave the page, so protected content must stop
  // rendering. Without this a user rejected by PrivateComponent calls logout() during
  // render, signoutRedirect() re-renders that same branch, and logout fires again before
  // navigation completes.
  //
  // `signinSilent` is excluded on purpose. automaticSilentRenew is on, so folding a
  // background token refresh into isLoading would blank the app on every renewal.
  const isRedirecting =
    auth.activeNavigator === "signinRedirect" || auth.activeNavigator === "signoutRedirect";

  return {
    // oidc-client-ts keeps ID token claims under `profile`.
    user: auth.user ? auth.user.profile : null,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading || isRedirecting,
    getAccessToken,
    login,
    logout: doLogout,
  };
}

/**
 * Picked once at module load, not per render: REACT_APP_AUTH_PROVIDER is inlined at
 * build time, so a build only ever mounts one provider and only ever calls that
 * adapter. Keeps hook order stable.
 */
const useAuthImpl = isZitadel ? useZitadelAdapter : useAuth0Adapter;

export const useAuth = () => useAuthImpl();
